/**
 * hand-strength.ts — 精确牌力评估器(整条内容管线唯一的牌力真值源)。
 *
 * Oracle 裁决:做**全的精确评估器**,不要启发式近似——启发式在「最大同花 vs 同花 /
 * 中对 vs 底对 / 顺子 vs 卡顺听」恰恰错得最狠,这三类正是新手信任崩塌点。
 * /train · /coach 解说 · 题库校验 · A2 语义闸门都复用本模块。
 *
 * 标准德州扑克牌力:hero 2 张 + board 5 张,**7 张枚举 C(7,5)=21 组选最佳 5 张**。
 * board 为空(preflop)→ 降级:只评估手牌(高牌/对子),classZh 标"翻前"语义,
 * draws 空,isNut=false。
 *
 * 纯函数、零外部依赖:只 import ./poker-cards,不读文件/不读 env/不引其它 lib。
 */

import { parseCards } from "./poker-cards";
import type { PokerCard } from "./poker-cards";

/** 九类牌力(低→高),数组顺序即牌力序。 */
export const HAND_CLASSES = [
  "high_card",
  "pair",
  "two_pair",
  "three_of_a_kind",
  "straight",
  "flush",
  "full_house",
  "four_of_a_kind",
  "straight_flush",
] as const;
export type HandClass = (typeof HAND_CLASSES)[number];

/** 听牌(未成手时)。 */
export type Draw =
  | "flush_draw"
  | "open_ended_straight_draw"
  | "gutshot";

export type HandStrength = {
  class: HandClass;
  /** 精确中文细分(顶对/中对/底对/超对、五高顺/百老汇/K高顺、坚果同花 等)。 */
  classZh: string;
  /** 构成最佳成手的公共牌 rank(去重、高→低)。preflop 为空。 */
  madeWithBoardRanks: string[];
  /** 听牌(仅未成手 high_card/pair/two_pair/three_of_a_kind 时可能非空)。 */
  draws: Draw[];
  /** 是否当前面坚果(最大可能牌力)。 */
  isNut: boolean;
};

const RANK_VALUE: Record<string, number> = {
  "2": 2,
  "3": 3,
  "4": 4,
  "5": 5,
  "6": 6,
  "7": 7,
  "8": 8,
  "9": 9,
  T: 10,
  J: 11,
  Q: 12,
  K: 13,
  A: 14,
};

const VALUE_RANK: Record<number, string> = Object.fromEntries(
  Object.entries(RANK_VALUE).map(([r, v]) => [v, r]),
);

const CLASS_RANK: Record<HandClass, number> = Object.fromEntries(
  HAND_CLASSES.map((c, i) => [c, i]),
) as Record<HandClass, number>;

type Suit = PokerCard["suit"];
type Tagged = PokerCard & { board: boolean };

/** 5 张牌分类结果(可比)。 */
type Five = {
  cls: HandClass;
  /** 同类内比较的 tiebreak(降序相关 rank 值)。 */
  tb: number[];
  /** 顺子/同花顺的高牌值;wheel=5,百老汇=14;非顺为 null。 */
  sh: number | null;
  /** 同花/同花顺的花色;非同花为 null。 */
  flushSuit: Suit | null;
};

/** 给定去重 rank 值(A=14,内部把 A 也视作 1 以支持 wheel),返回最高顺子高牌值,无则 null。 */
function straightHigh(distinctVals: number[]): number | null {
  const s = new Set(distinctVals);
  if (s.has(14)) s.add(1);
  for (let high = 14; high >= 5; high--) {
    let ok = true;
    for (let k = 0; k < 5; k++) {
      if (!s.has(high - k)) {
        ok = false;
        break;
      }
    }
    if (ok) return high;
  }
  return null;
}

/** 精确分类一手 5 张牌。 */
function classify5(cards: PokerCard[]): Five {
  const vals = cards.map((c) => RANK_VALUE[c.rank]).sort((a, b) => b - a);
  const distinct = [...new Set(vals)];
  const flushSuit =
    cards.every((c) => c.suit === cards[0].suit) && cards[0].suit !== "x"
      ? cards[0].suit
      : null;
  const sh = straightHigh(distinct);

  const counts = new Map<number, number>();
  for (const v of vals) counts.set(v, (counts.get(v) ?? 0) + 1);
  // 按(张数降序, rank 降序)排:groups[0] 是最大组。
  const groups = [...counts.entries()].sort(
    (a, b) => b[1] - a[1] || b[0] - a[0],
  );
  const [g0, g1] = [groups[0], groups[1]];

  if (flushSuit !== null && sh !== null)
    return { cls: "straight_flush", tb: [sh], sh, flushSuit };
  if (g0[1] === 4)
    return { cls: "four_of_a_kind", tb: [g0[0], g1[0]], sh: null, flushSuit: null };
  if (g0[1] === 3 && g1 && g1[1] >= 2)
    return { cls: "full_house", tb: [g0[0], g1[0]], sh: null, flushSuit: null };
  if (flushSuit !== null)
    return { cls: "flush", tb: vals, sh: null, flushSuit };
  if (sh !== null) return { cls: "straight", tb: [sh], sh, flushSuit: null };
  if (g0[1] === 3)
    return {
      cls: "three_of_a_kind",
      tb: [g0[0], groups[1][0], groups[2][0]],
      sh: null,
      flushSuit: null,
    };
  if (g0[1] === 2 && g1 && g1[1] === 2)
    return {
      cls: "two_pair",
      tb: [g0[0], g1[0], groups[2][0]],
      sh: null,
      flushSuit: null,
    };
  if (g0[1] === 2)
    return {
      cls: "pair",
      tb: [g0[0], groups[1][0], groups[2][0], groups[3][0]],
      sh: null,
      flushSuit: null,
    };
  return { cls: "high_card", tb: vals, sh: null, flushSuit: null };
}

/** 比较键:[牌力序, ...tiebreak];lex 大者胜。 */
function scoreKey(f: Five): number[] {
  return [CLASS_RANK[f.cls], ...f.tb];
}

function lexGreater(a: number[], b: number[]): boolean {
  const n = Math.max(a.length, b.length);
  for (let i = 0; i < n; i++) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x !== y) return x > y;
  }
  return false;
}

function combinations<T>(arr: T[], k: number): T[][] {
  const res: T[][] = [];
  const rec = (start: number, cur: T[]) => {
    if (cur.length === k) {
      res.push([...cur]);
      return;
    }
    for (let i = start; i < arr.length; i++) {
      cur.push(arr[i]);
      rec(i + 1, cur);
      cur.pop();
    }
  };
  rec(0, []);
  return res;
}

function countByRank(cards: PokerCard[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const c of cards) {
    const v = RANK_VALUE[c.rank];
    m.set(v, (m.get(v) ?? 0) + 1);
  }
  return m;
}

function rankGroups(cards: PokerCard[]): Array<[number, number]> {
  return [...countByRank(cards).entries()].sort(
    (a, b) => b[1] - a[1] || b[0] - a[0],
  );
}

/**
 * 坚果顺子高牌:给定公共牌 rank,hero 可补任意 2 张时可达到的最高顺子高牌值。
 * 5 连窗口中缺 ≤2 张(由 hero 补)即可达成;取最高。无则 -1。
 */
function nutStraightHigh(boardVals: number[]): number {
  const s = new Set(boardVals);
  if (s.has(14)) s.add(1);
  for (let high = 14; high >= 5; high--) {
    let missing = 0;
    for (let k = 0; k < 5; k++) if (!s.has(high - k)) missing++;
    if (missing <= 2) return high;
  }
  return -1;
}

/**
 * 坚果葫芦 (tripRank, pairRank):给定公共牌点数分布,hero 恰 2 张可达到的最高葫芦。
 * hero 槽位预算精确:补三条需 max(0,3-bt) 张、补对子需 max(0,2-bp) 张,合计 ≤2。
 */
function nutFullHouse(
  boardCounts: Map<number, number>,
): [number, number] | null {
  let best: [number, number] | null = null;
  for (let t = 14; t >= 2; t--) {
    const bt = boardCounts.get(t) ?? 0;
    if (bt < 1) continue;
    const heroTrips = Math.max(0, 3 - bt);
    if (heroTrips > 2) continue;
    for (let p = 14; p >= 2; p--) {
      if (p === t) continue;
      const bp = boardCounts.get(p) ?? 0;
      const heroPair = Math.max(0, 2 - bp);
      if (heroTrips + heroPair > 2) continue;
      if (best === null || t > best[0] || (t === best[0] && p > best[1])) {
        best = [t, p];
      }
    }
  }
  return best;
}

/** 坚果四条 rank:公共牌中点数 ≥2 张的最高 rank(hero 可补其余成四条)。无则 -1。 */
function nutQuadRank(boardCounts: Map<number, number>): number {
  let best = -1;
  for (const [v, n] of boardCounts) if (n >= 2 && v > best) best = v;
  return best;
}

/** 顺子听牌检测(基于全部可用牌的 rank 集合)。返回 open_ended / gutshot / null。 */
function straightDraw(allVals: number[]): Draw | null {
  const s = new Set(allVals);
  if (s.has(14)) s.add(1);
  const has = (v: number) => s.has(v);
  // 开放两头顺听:存在 4 连续 r..r+3,且两端 r-1、r+4 均为有效 rank(可作 outs)。
  for (let r = 1; r <= 10; r++) {
    if (has(r) && has(r + 1) && has(r + 2) && has(r + 3)) {
      if (r - 1 >= 1 && r + 4 <= 14) return "open_ended_straight_draw";
    }
  }
  // 卡顺听:某 5 连窗口恰缺 1 张(且未构成开放听)。
  for (let w = 1; w <= 10; w++) {
    let present = 0;
    for (let k = 0; k < 5; k++) if (has(w + k)) present++;
    if (present === 4) return "gutshot";
  }
  return null;
}

function parseTwo(input: string, label: string): PokerCard[] {
  const cards = parseCards(input);
  if (!cards || cards.length !== 2)
    throw new Error(`hand-strength: ${label} 需恰 2 张牌(紧凑式如 "Ad7s"),收到 "${input}"`);
  return cards;
}

/**
 * 精确牌力评估。hero 2 张 + board 5 张(7 选最佳 5);board 空 → preflop 降级。
 * @param heroHand 英雄手牌紧凑串,如 "Ad7s" / "AsAh"。
 * @param board 公共牌紧凑串,如 "Kd7s2c9h4d";空串/null → 翻前。
 */
export function evaluateHandStrength(
  heroHand: string,
  board: string,
): HandStrength {
  const hero = parseTwo(heroHand, "heroHand");
  const boardCards = board && board.trim() ? parseCards(board) : null;

  // ── preflop 降级 ──────────────────────────────────────────────
  if (!boardCards || boardCards.length === 0) {
    const isPair = hero[0].rank === hero[1].rank;
    return {
      class: isPair ? "pair" : "high_card",
      classZh: isPair ? "翻前对子" : "翻前高牌",
      madeWithBoardRanks: [],
      draws: [],
      isNut: false,
    };
  }

  const tagged: Tagged[] = [
    ...hero.map((c) => ({ ...c, board: false })),
    ...boardCards.map((c) => ({ ...c, board: true })),
  ];

  // 不足 5 张(异常输入)→ 退化为翻前式降级。
  if (tagged.length < 5) {
    const isPair = hero[0].rank === hero[1].rank;
    return {
      class: isPair ? "pair" : "high_card",
      classZh: isPair ? "翻前对子" : "翻前高牌",
      madeWithBoardRanks: [],
      draws: [],
      isNut: false,
    };
  }

  // ── 7 选最佳 5(精确枚举)────────────────────────────────────
  let bestCards: Tagged[] = [];
  let bestFive: Five | null = null;
  let bestKey: number[] = [];
  for (const combo of combinations(tagged, 5)) {
    const five = classify5(combo);
    const key = scoreKey(five);
    if (bestFive === null || lexGreater(key, bestKey)) {
      bestFive = five;
      bestKey = key;
      bestCards = combo;
    }
  }
  const info = bestFive as Five;
  const cls = info.cls;

  // ── madeWithBoardRanks(去重、高→低)─────────────────────────
  const usedBoardVals = new Set(
    bestCards.filter((c) => c.board).map((c) => RANK_VALUE[c.rank]),
  );
  const madeWithBoardRanks = [...usedBoardVals]
    .sort((a, b) => b - a)
    .map((v) => VALUE_RANK[v]);

  // ── classZh 精确细分 ─────────────────────────────────────────
  const boardVals = boardCards.map((c) => RANK_VALUE[c.rank]);
  const maxBoard = Math.max(...boardVals);
  const minBoard = Math.min(...boardVals);
  const boardValSet = new Set(boardVals);
  const heroPocket = hero[0].rank === hero[1].rank;
  const groups = rankGroups(bestCards);
  const pairRankVal = groups.find((g) => g[1] === 2)?.[0] ?? -1;

  let classZh: string;
  switch (cls) {
    case "straight_flush":
      classZh =
        info.sh === 14
          ? "皇家同花顺"
          : info.sh === 5
            ? "五高同花顺（轮子）"
            : "同花顺";
      break;
    case "four_of_a_kind":
      classZh = "四条";
      break;
    case "full_house":
      classZh = "葫芦";
      break;
    case "flush":
      classZh = hero.some(
        (c) => c.rank === "A" && c.suit === info.flushSuit,
      )
        ? "坚果同花"
        : "同花";
      break;
    case "straight":
      classZh =
        info.sh === 5
          ? "五高顺（轮子）"
          : info.sh === 14
            ? "百老汇"
            : `${VALUE_RANK[info.sh as number]}高顺`;
      break;
    case "three_of_a_kind":
      classZh = "三条";
      break;
    case "two_pair":
      classZh = "两对";
      break;
    case "pair": {
      if (
        heroPocket &&
        RANK_VALUE[hero[0].rank] === pairRankVal &&
        pairRankVal > maxBoard
      ) {
        classZh = "超对（overpair）";
      } else if (pairRankVal === maxBoard) {
        classZh = "顶对";
      } else if (pairRankVal === minBoard) {
        classZh = "底对";
      } else if (boardValSet.has(pairRankVal)) {
        classZh = "中对";
      } else {
        classZh = "对子";
      }
      break;
    }
    case "high_card":
      classZh = "高牌";
      break;
  }

  // ── draws(仅未成手:high_card/pair/two_pair/three_of_a_kind)──
  const draws: Draw[] = [];
  if (CLASS_RANK[cls] < CLASS_RANK.straight) {
    const allCards = tagged as PokerCard[];
    // 同花听:某花色恰 4 张(成手同花已被 cls 闸门排除)。
    const suitCount = new Map<Suit, number>();
    for (const c of allCards) {
      if (c.suit === "x") continue;
      suitCount.set(c.suit, (suitCount.get(c.suit) ?? 0) + 1);
    }
    for (const n of suitCount.values()) {
      if (n === 4) {
        draws.push("flush_draw");
        break;
      }
    }
    const sd = straightDraw(allCards.map((c) => RANK_VALUE[c.rank]));
    if (sd) draws.push(sd);
  }

  // ── isNut(坚果判定)────────────────────────────────────────
  const boardCounts = countByRank(boardCards);
  let isNut = false;
  switch (cls) {
    case "straight_flush":
    case "straight":
      isNut = info.sh === nutStraightHigh(boardVals);
      break;
    case "flush":
      isNut = hero.some((c) => c.rank === "A" && c.suit === info.flushSuit);
      break;
    case "full_house": {
      const nut = nutFullHouse(boardCounts);
      isNut =
        nut !== null &&
        groups[0][0] === nut[0] &&
        (groups[1]?.[0] ?? -1) === nut[1];
      break;
    }
    case "four_of_a_kind":
      isNut = groups[0][0] === nutQuadRank(boardCounts);
      break;
    default:
      isNut = false;
  }

  return { class: cls, classZh, madeWithBoardRanks, draws, isNut };
}
